import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { intersection, isEmpty } from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import PonduranceEmailButton from '@/components/PonduranceEmailButton';
import PonduranceLinkButton from '@/components/PonduranceLinkButton';
import { INTERNAL_DOMAIN_REGEX } from '@/constants/common';
import {
  TOOLTIP_TITLE_TICKET_WATCHERS_SERVICE,
  TOOLTIP_TITLE_TICKET_WATCHERS_THREAT
} from '@/features/TicketWatchers/TicketWatchers.constants';
import {
  convertLogscaleLinks,
  sanitizeMarkdownContent
} from '@/utils/Common.utils';

import {
  LOGSCALE_URL_REGEX,
  MARKDOWN_ESCAPED_PUNCTUATION_REGEX,
  TAGS_REGEX,
  TICKET_TYPE,
  URL_REGEX
} from './Ticket.constants';
import type {
  RecommendationCreateDTO,
  Ticket,
  TicketDTO,
  TicketWatcherDTO,
  Watcher,
  WatcherDTO
} from './Ticket.types';

dayjs.extend(utc);

// used to add/remove markdown characters from urls
export const formatUrl = (action: 'add' | 'remove', url: string) => {
  let formattedUrl = url;
  // adds necessary markdown to urls to display properly in the markdown editor
  if (formattedUrl && action === 'add') {
    formattedUrl = (formattedUrl as string).replace('\\', () => '\\\\');
  }

  // removes necessary markdown from urls to save raw url to db
  if (formattedUrl && action === 'remove') {
    formattedUrl = (formattedUrl as string).replace(
      MARKDOWN_ESCAPED_PUNCTUATION_REGEX,
      '$1'
    );
  }

  return formattedUrl;
};

export const mdxFormatDescriptionSummary = (descriptionSummary: string) => {
  // to display urls in the markdown editor, we need to add markdown escaped characters
  const mdxFormattedDescriptionSummaryUrls = descriptionSummary.replace(
    URL_REGEX,
    (match: string) => {
      return formatUrl('add', match);
    }
  );

  // to display tags in the markdown editor, we need to add double backslashes
  const mdxFormattedDescriptionSummary =
    mdxFormattedDescriptionSummaryUrls.replace(TAGS_REGEX, (match: string) => {
      return `\\${match}`;
    });

  return mdxFormattedDescriptionSummary;
};

export const formatConversationMessage = (message: string) => {
  const delimiter = '^__^';

  // handle formatting of message
  const messageReplacedCommas = message.replace(/, /g, ` ,${delimiter}`);
  const messageReplacedNewlines = messageReplacedCommas.replace(
    /\n/g,
    ` \n${delimiter}`
  );
  const messageReplacedCarriageReturns = messageReplacedNewlines.replace(
    /\r/g,
    ` \r${delimiter}`
  );
  const messageReplacedSpaces = messageReplacedCarriageReturns.replace(
    / /g,
    delimiter
  );

  const stringArray = messageReplacedSpaces.split(delimiter);

  return (
    <>
      {stringArray.map((string, i) => {
        // is pondurance email
        if (string.includes('@pondurance.com')) {
          return (
            <React.Fragment key={uuidv4()}>
              <PonduranceEmailButton key={uuidv4()} email={string} />{' '}
            </React.Fragment>
          );
        }
        // is pondurance domain
        if (string.match(INTERNAL_DOMAIN_REGEX)) {
          // is pondurance logscale domain (conversion to sso url)
          if (typeof string === 'string' && string.match(LOGSCALE_URL_REGEX)) {
            return (
              <React.Fragment key={uuidv4()}>
                <PonduranceLinkButton
                  key={uuidv4()}
                  url={convertLogscaleLinks(string)}
                />{' '}
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={uuidv4()}>
              <PonduranceLinkButton url={string} />{' '}
            </React.Fragment>
          );
        }
        return stringArray[i + 1] === '\n' ||
          stringArray[i + 1] === '\r' ||
          stringArray[i + 1] === ','
          ? string
          : `${string} `;
      })}
    </>
  );
};

export const mapTicketWatchersDTO = (
  watchers: TicketWatcherDTO[]
): Watcher[] => {
  const filteredWatchers = watchers.filter((watcher) => watcher?.user.active);

  return filteredWatchers.map((watcher) => ({
    id: watcher.id,
    internal: !!watcher.user?.internal,
    label: watcher.user?.name,
    notificationAnnouncement: watcher.user.notification_announcement,
    notificationThreatPriority: watcher.user.notification_threat_priority,
    notificationTicketService: watcher.user.notification_ticket_service,
    notificationTicketThreat: watcher.user.notification_ticket_threat,
    notificationTags: watcher.user.notification_tags,
    subLabel: watcher.user?.email,
    userId: watcher.user?.id,
    value: watcher.user?.name,
    organizationIds: []
  }));
};

export const mapWatchersDTO = (watchers: WatcherDTO[]): Watcher[] => {
  return watchers.map((watcher) => ({
    id: watcher.id,
    internal: !!watcher.internal,
    label: watcher.name,
    notificationAnnouncement: watcher.notification_announcement,
    notificationThreatPriority: watcher.notification_threat_priority,
    notificationTicketService: watcher.notification_ticket_service,
    notificationTicketThreat: watcher.notification_ticket_threat,
    notificationTags: watcher.notification_tags,
    subLabel: watcher.email,
    userId: watcher.id,
    value: `${watcher.id}`,
    organizationIds: watcher.organizations.map((org) => org.id)
  }));
};

export const mapTicketDTO = (ticket: TicketDTO): Ticket => {
  return {
    closed_at: ticket.closed_at,
    acknowledged_at: ticket.acknowledged_at,
    contained_at: ticket.contained_at,
    remediated_at: ticket.remediated_at,
    id: ticket.id,
    additional_details: ticket.additional_details,
    alert_source: ticket.alert_source,
    assignee: ticket?.assignee,
    activities: ticket.activities,
    attachments: ticket.attachments,
    auto_close_enabled: ticket.auto_close_enabled,
    external_ticket_id: ticket.external_ticket_id || undefined,
    followup_date: ticket.followup_date,
    feedback_rating: ticket.feedback_rating,
    feedback_reason: ticket.feedback_reason,
    investigate_url: ticket.investigate_url || '',
    ip_address: '',
    last_updated_at: ticket.last_updated_at,
    created_at: ticket.created_at,
    custom_data: ticket.custom_data,
    customer_ticket_id: ticket.customer_ticket_id || undefined,
    human_soc_requestable: ticket.human_soc_requestable,
    detected_at: ticket.detected_at,
    ingested_at: ticket.ingested_at,
    origination_source: ticket.origination_source,
    updated_at: ticket.updated_at,
    messages: ticket.messages,
    next_action: ticket.next_action,
    occurred_at: ticket.occurred_at,
    organization: ticket.organization,
    owner: ticket.owner,
    published_at: ticket.published_at,
    resolution_id: ticket.resolution_id,
    sub_category: ticket.sub_category,
    summary: ticket.summary,
    turbine_case_number: ticket.turbine_case_number,
    turbine_record_id: ticket.turbine_record_id,
    team_queue: ticket.team_queue,
    ticket_category: ticket.ticket_category,
    ticket_external_threats: ticket.ticket_external_threats,
    ticket_priority: ticket.ticket_priority,
    ticket_recommendations: ticket.ticket_recommendations,
    ticket_response_actions: ticket.ticket_response_actions,
    ticket_status: ticket.ticket_status,
    ticket_type: ticket.ticket_type,
    ticket_watchers: mapTicketWatchersDTO(ticket.ticket_watchers || []),
    time_to_notify:
      ticket.published_at && ticket.ingested_at
        ? new Date(ticket.published_at).getTime() -
          new Date(ticket.ingested_at).getTime()
        : 0,
    time_to_detect:
      ticket.detected_at && ticket.ingested_at
        ? new Date(ticket.detected_at).getTime() -
          new Date(ticket.ingested_at).getTime()
        : 0,
    time_to_acknowledge:
      ticket.published_at && ticket.acknowledged_at
        ? new Date(ticket.acknowledged_at).getTime() -
          new Date(ticket.published_at).getTime()
        : 0,
    time_to_remediate:
      ticket.published_at && ticket.remediated_at
        ? new Date(ticket.remediated_at).getTime() -
          new Date(ticket.published_at).getTime()
        : 0,
    time_to_close:
      ticket.published_at && ticket.closed_at
        ? new Date(ticket.closed_at).getTime() -
          new Date(ticket.published_at).getTime()
        : 0,
    title: ticket.title,
    user: ticket.user,
    tags: ticket.tags || []
  };
};

export const mapTicketToTicketCreate = (ticket: Ticket) => {
  // threat tickets
  if (ticket.ticket_type === 1) {
    return {
      additional_details: ticket.additional_details,
      assignee_id: ticket.assignee?.id,
      attachments: ticket.attachments,
      category_id: ticket.ticket_category,
      customer_ticket_id: ticket.customer_ticket_id,
      external_ticket_id: ticket.external_ticket_id,
      investigate_url: ticket.investigate_url,
      followup_date: dayjs(ticket.followup_date).utc().format(),
      next_action: ticket.next_action,
      detected_at: dayjs(ticket.detected_at).utc().format(),
      organization_id: ticket.organization,
      owner_id: ticket.owner === '' ? null : ticket.owner,
      priority_id: ticket.ticket_priority,
      summary: sanitizeMarkdownContent(ticket.summary),
      team_queue: ticket.team_queue,
      ticket_external_threats: ticket.ticket_external_threats,
      ticket_recommendations:
        ticket.ticket_recommendations as RecommendationCreateDTO[],
      ticket_type: 1,
      ticket_watchers: ticket.ticket_watchers.map((watcher) => ({
        user_id: watcher.userId
      })),
      title: ticket.title
    };
  }

  // service tickets
  if (ticket.ticket_type === 2) {
    return {
      additional_details: ticket.additional_details,
      assignee_id: ticket.assignee?.id,
      attachments: ticket.attachments,
      category_id: ticket.ticket_category,
      custom_data: ticket.custom_data,
      customer_ticket_id: ticket.customer_ticket_id,
      external_ticket_id: ticket.external_ticket_id,
      followup_date: dayjs(ticket.followup_date).utc().format(),
      next_action: ticket.next_action,
      organization_id: ticket.organization_id,
      owner_id: ticket.owner_id,
      priority_id: ticket.ticket_priority,
      sub_category:
        ticket.ticket_subcategory === '' ? null : ticket.ticket_subcategory,
      summary: sanitizeMarkdownContent(ticket.summary),
      tags: ticket.tags,
      team_queue: ticket.team_queue,
      ticket_type: 2,
      ticket_watchers: ticket.ticket_watchers.map((watcher) => ({
        user_id: watcher.userId
      })),
      title: ticket.title
    };
  }

  return null;
};

export const mapTicketToTicketUpdate = (ticket: Ticket) => {
  // threat tickets
  if (ticket.ticket_type === 1) {
    return {
      additional_details: ticket.additional_details,
      assignee_id: ticket.assignee,
      attachments: ticket.attachments,
      category_id: ticket.ticket_category,
      customer_ticket_id: ticket.customer_ticket_id,
      external_ticket_id: ticket.external_ticket_id,
      investigate_url: ticket.investigate_url,
      followup_date: dayjs(ticket.followup_date).utc().format(),
      next_action: ticket.next_action,
      messages: ticket.messages,
      detected_at: dayjs(ticket.detected_at).utc().format(),
      owner_id: ticket.owner,
      priority_id: ticket.ticket_priority,
      summary: sanitizeMarkdownContent(ticket.summary),
      team_queue: ticket.team_queue,
      ticket_external_threats: ticket.ticket_external_threats,
      ticket_recommendations:
        ticket.ticket_recommendations as RecommendationCreateDTO[],
      ticket_type: 1,
      ticket_watchers: ticket.ticket_watchers.map((watcher) => ({
        id: watcher.id,
        user_id: watcher.userId
      })),
      title: ticket.title
    };
  }

  // service tickets
  if (ticket.ticket_type === 2) {
    return {
      additional_details: ticket.additional_details,
      assignee_id: ticket.assignee,
      attachments: ticket.attachments,
      category_id: ticket.ticket_category,
      customer_ticket_id: ticket.customer_ticket_id,
      external_ticket_id: ticket.external_ticket_id,
      followup_date: dayjs(ticket.followup_date).utc().format(),
      next_action: ticket.next_action,
      owner_id: ticket.owner,
      priority_id: ticket.ticket_priority,
      summary: sanitizeMarkdownContent(ticket.summary),
      team_queue: ticket.team_queue,
      ticket_type: 2,
      ticket_watchers: ticket.ticket_watchers.map((watcher) => ({
        id: watcher.id,
        user_id: watcher.userId
      })),
      title: ticket.title
    };
  }

  return null;
};

export const setFollowupDate = (
  followup_date: Dayjs | string | null | undefined
) => {
  const followupDate = dayjs
    .utc(followup_date)
    .set('hour', 21)
    .set('minute', 0)
    .set('second', 0);
  return followupDate;
};

export const hasOrganizationWatchers = (
  watcherIds: number[],
  allWatchers: WatcherDTO[],
  orgId: number
) => {
  const orgWatcherOptions = allWatchers
    .filter((w) => w.organizations.some((org) => org.id === orgId))
    .map((w) => w.id);

  return watcherIds.some((w) => orgWatcherOptions.includes(w));
};

export const displayIcon = (
  watcher: Watcher,
  ticket_priority: number,
  ticket_type: number,
  tags: string[] = []
) => {
  if (!watcher.internal && watcher.userId !== 0) {
    // tags ticket
    if (
      !isEmpty(watcher.notificationTags) &&
      !isEmpty(tags) &&
      intersection(watcher.notificationTags, tags).length > 0
    ) {
      return false;
    }

    // threat ticket
    if (
      ticket_priority &&
      ticket_type === 1 &&
      (!watcher.notificationTicketThreat ||
        !watcher.notificationThreatPriority.includes(ticket_priority))
    ) {
      return true;
    }

    // service ticket
    if (ticket_type === 2 && !watcher.notificationTicketService) {
      return true;
    }
  }
  return false;
};

export const getIconTooltipTitle = (
  watcher: Watcher,
  ticket_priority: number,
  ticket_type: number
) => {
  if (ticket_type === 1) {
    if (!watcher.notificationTicketThreat) {
      // threat ticket icon tooltip title
      return TOOLTIP_TITLE_TICKET_WATCHERS_THREAT;
    }
    // threat ticket priority icon tooltip title
    return `User prefers no P${ticket_priority} Threat Ticket notifications.`;
  }
  // service ticket icon tooltip title
  return TOOLTIP_TITLE_TICKET_WATCHERS_SERVICE;
};

export const isSilencedTicketOrgWatcherThreat = (
  ticketPriority: number,
  ticketType: number,
  watcher: Watcher
) => {
  return (
    !watcher.internal &&
    ticketType === TICKET_TYPE.THREAT &&
    (!watcher.notificationTicketThreat ||
      !watcher.notificationThreatPriority.includes(ticketPriority || 0))
  );
};

export const isSilencedTicketOrgWatcherService = (
  ticketType: number,
  watcher: Watcher
) => {
  return (
    !watcher.internal &&
    ticketType === TICKET_TYPE.SERVICE &&
    !watcher.notificationTicketService
  );
};
